// src/services/tarjetaOrcamento.js
// Tarjeta "Crear orçamento" del Cotizador (imagen para el cliente). Funciones
// puras, sin DOM: construirTarjeta arma el modelo y tarjetaHtml lo dibuja con
// la plantilla aprobada (plantilla-tarjeta-cliente.md: 1080 px, Poppins,
// #064985 / #0A63B3 / #F46D0D). Nada se inventa: si falta un dato, esa línea
// no se imprime y el dato aparece en `faltantes` para avisar en la vista previa.
//
// NUNCA entra a la tarjeta: ganancia, costo, comisión BRT, hostal, precio por
// fuente ni milheiro. Solo se lee de la opción lo que es del viaje.
import { diaNum, isoDeDiaNum, minDeHora, minAbs, fmtBRL, esNum, masDiasLlegada } from './cotizadorCalc';
import { aeropuertoDeCiudad, aeropuertoSalidaDe } from './aeropuertos';

export const MAX_OPCIONES = 3;
export const MODOS = { basico: 'Básico', detallado: 'Detallado' };

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const txt = (v) => String(v ?? '').trim();

// Las fechas son 'YYYY-MM-DD' de calendario (sin hora ni zona): el día de la
// semana sale de la cuenta de días, así que America/Sao_Paulo no puede correrlo.
export function diaSemanaLargo(iso) {
  const n = diaNum(iso);
  return n === null ? '' : DIAS[(n + 4) % 7]; // el día 0 (1/1/1970) fue jueves
}
// "1/11/2026" -> "domingo 1 de noviembre" · conCap -> "Domingo 1 de noviembre"
export function fechaLarga(iso, conCap = false) {
  const n = diaNum(iso);
  if (n === null) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const t = `${diaSemanaLargo(iso)} ${d} de ${MESES[m - 1]}`;
  return conCap ? cap(t) : t;
}
const fechaDiaMes = (iso) => {
  if (diaNum(iso) === null) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} de ${MESES[m - 1]}`;
};

// "Campinas (São Paulo)" -> "Campinas" · "Florianópolis + ómnibus" -> "Florianópolis"
export const ciudadCorta = (c) => txt(c).replace(/\s*\+.*$/, '').replace(/\s*\(.*?\)/g, '').trim();
const ciudadTitulo = (c) => txt(c).replace(/\s*\+.*$/, '').trim();
const norm = (c) => ciudadCorta(c).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const reales = (v) => `R$ ${fmtBRL(v, 2)}`;
const dolares = (v) => `US$ ${fmtBRL(v, 2)}`;

// Día en que llega el vuelo: el mismo, o el siguiente si la hora de llegada es menor que la de salida.
function diaLlegadaVuelo(op) {
  if (diaNum(op.dia_vuelo) === null) return null;
  return isoDeDiaNum(diaNum(op.dia_vuelo) + masDiasLlegada(op));
}

const escalasTxt = (e) => (e === 0 ? 'directo' : e === 1 ? '1 escala, todo en el mismo boleto' : `${e} escalas, todo en el mismo boleto`);

// ── Pasos de una opción ──────────────────────────────────────────────
function pasosDe({ opcion: op, resultado: r }, modo, faltan) {
  const det = modo === 'detallado';
  const falta = (t) => { if (!faltan.includes(t)) faltan.push(t); };
  const destino = ciudadTitulo(op.ciudad);
  const destCorto = ciudadCorta(op.ciudad);
  const pasos = [];
  const paso = (clave, tipo, titulo, lineas) => pasos.push({
    clave, tipo, titulo, lineas: lineas.filter(Boolean), texto: [titulo, ...lineas.filter(Boolean)].join(' · '),
  });

  // 1 · Ómnibus Boa Vista → Manaus
  if (r.bus) {
    const b = r.bus;
    const l = [];
    if (det) {
      const llega = b.llegaIso && b.llegaIso !== b.salidaIso ? `llega el ${fechaLarga(b.llegaIso)} a las ${b.llega}` : `llega a las ${b.llega}`;
      l.push(`${fechaLarga(b.salidaIso, true)} · sale a las ${b.sale}${b.llega ? ` y ${llega}` : ''}`);
      const partes = [b.empresa, b.terminalOrigen || b.terminalDestino ? `${b.terminalOrigen || '—'} → ${b.terminalDestino || '—'}` : ''];
      l.push(partes.filter(Boolean).join(' · '));
      if (!b.empresa) falta('empresa del ómnibus');
    } else {
      l.push(fechaLarga(b.salidaIso, true));
    }
    paso('bus1', 'bus', 'Ómnibus: Boa Vista → Manaus', l);
  }

  // 2 · Espera en Manaus
  if (r.bus) paso('espera1', 'espera', 'Espera en el aeropuerto de Manaus', []);

  // 3 · Vuelo (desde Manaus, o desde Boa Vista si el pasajero vuela directo de ahí)
  const origenCiudad = op.sale_de === 'BVB' ? 'Boa Vista' : 'Manaus';
  const dia = op.dia_vuelo;
  const l1 = [fechaLarga(dia, true)];
  const salida = txt(op.hora_vuelo);
  const llegada = txt(op.hora_llegada_vuelo);
  const diaLlega = diaLlegadaVuelo(op);
  // Si llega otro día se dice cuál, para que nadie piense que es un vuelo corto.
  if (salida && llegada) l1.push(diaLlega && diaLlega !== dia ? `sale a las ${salida} y llega el ${fechaLarga(diaLlega)} a las ${llegada} (horas locales)` : `sale a las ${salida} y llega a las ${llegada} (horas locales)`);
  else if (salida) l1.push(`sale a las ${salida}`);
  if (!salida) falta('hora de salida del vuelo');
  if (!llegada) falta('hora de llegada del vuelo');
  const escalas = esNum(op.escalas) ? Number(op.escalas) : null;
  const aero = txt(op.aerolinea);
  if (!aero) falta('aerolínea');
  if (escalas === null) falta('escalas');
  const equipaje = txt(op.equipaje) || 'sin equipaje despachado';
  const l2 = [[aero, escalas === null ? '' : escalasTxt(escalas)].filter(Boolean).join(', '), equipaje].filter(Boolean).join(' · ');
  const lineasVuelo = [l1.join(' · '), l2];
  if (det) {
    const nv = txt(op.numero_vuelo);
    if (!nv) falta('número de vuelo');
    if (nv) lineasVuelo.push(`Vuelo ${nv}`);
    // Aeropuertos: lo escrito a mano manda; si no, se deduce (salida: Manaus o Boa Vista; llegada: por la ciudad).
    const apS = txt(op.aeropuerto_salida) || aeropuertoSalidaDe(op);
    const apL = txt(op.aeropuerto_llegada) || aeropuertoDeCiudad(op.ciudad);
    const aS = [apS, txt(op.terminal_salida) ? `terminal ${txt(op.terminal_salida)}` : ''].filter(Boolean).join(', ');
    const aL = [apL, txt(op.terminal_llegada) ? `terminal ${txt(op.terminal_llegada)}` : ''].filter(Boolean).join(', ');
    if (!apL) falta('aeropuerto de llegada');
    if (aS || aL) lineasVuelo.push(`${aS || origenCiudad} → ${aL || destCorto}`);
    if (txt(op.localizador)) lineasVuelo.push(`Localizador: ${txt(op.localizador)}`);
  }
  if (!destino) falta('ciudad de destino');
  paso('vuelo', 'vuelo', `Vuelo: ${origenCiudad} → ${destino || '…'}`, lineasVuelo);

  // 4 · 5 · 6 · Después del vuelo
  const extras = (Array.isArray(op.extras) ? op.extras : []).filter((e) => e && (txt(e.ciudad) || e.sale_fecha || e.sale_hora));
  if (extras.length) paso('espera2', 'espera', `Espera en el aeropuerto de ${destCorto || 'la ciudad de llegada'}`, []);
  if (op.traslado_incluido) paso('traslado', 'traslado', 'Traslado incluido', []);
  extras.forEach((e, i) => {
    const partes = txt(e.ciudad).split(/→|->/).map((x) => x.trim()).filter(Boolean);
    const origen = partes.length > 1 ? partes[0] : '';
    const final = partes[partes.length - 1] || destCorto;
    // El origen solo se nombra cuando es la misma ciudad a la que llegó el vuelo.
    const mismaCiudad = origen && destCorto && norm(origen) === norm(destCorto);
    const titulo = mismaCiudad
      ? `Ómnibus: aeropuerto de ${ciudadCorta(origen)} → aeropuerto de ${ciudadCorta(final)}`
      : `Ómnibus final hasta ${ciudadTitulo(final)}`;
    const diaBus = e.sale_fecha || diaLlegadaVuelo(op);
    const lineas = [];
    if (det) {
      const h = [txt(e.sale_hora) ? `sale a las ${txt(e.sale_hora)}` : '', txt(e.llega_hora) ? `llega a las ${txt(e.llega_hora)}` : ''].filter(Boolean).join(' y ');
      lineas.push([fechaLarga(diaBus, true), h].filter(Boolean).join(' · '));
      lineas.push([txt(e.empresa), txt(e.terminal_origen) || txt(e.terminal_destino) ? `${txt(e.terminal_origen) || '—'} → ${txt(e.terminal_destino) || '—'}` : ''].filter(Boolean).join(' · '));
      if (!txt(e.empresa)) falta('empresa del ómnibus extra');
      if (!txt(e.sale_hora)) falta('hora del ómnibus extra');
    } else lineas.push(fechaLarga(diaBus, true));
    paso(`bus${i + 2}`, 'bus', titulo, lineas);
  });

  // Textos corregidos a mano (se guardan en la opción, por modo).
  const ajustes = (op.tarjeta_ajustes && op.tarjeta_ajustes[modo]) || {};
  return pasos.map((p) => {
    const a = ajustes[p.clave];
    if (!a) return p;
    const titulo = a.titulo !== undefined ? a.titulo : p.titulo;
    const lineas = Array.isArray(a.lineas) ? a.lineas.map(txt).filter(Boolean) : p.lineas;
    return { ...p, titulo, lineas, texto: [titulo, ...lineas].join(' · '), editado: true };
  });
}

// Llegada final al destino (minutos absolutos) para saber cuál es la más rápida.
function llegadaFinalAbs(op) {
  const extras = (Array.isArray(op.extras) ? op.extras : []).filter((e) => e && e.llega_fecha && e.llega_hora);
  if (extras.length) return minAbs(extras[extras.length - 1].llega_fecha, extras[extras.length - 1].llega_hora);
  const d = diaLlegadaVuelo(op);
  return minAbs(d, op.hora_llegada_vuelo);
}
function llegadaFinalTxt(op) {
  const extras = (Array.isArray(op.extras) ? op.extras : []).filter((e) => e && e.llega_fecha);
  if (extras.length) return fechaLarga(extras[extras.length - 1].llega_fecha, true);
  return fechaLarga(diaLlegadaVuelo(op), true);
}

// ── Modelo de la tarjeta ─────────────────────────────────────────────
// opciones: [{ opcion, resultado }] (máximo 3). cliente: { tipo, usd, tasa, pasajeros }.
export function construirTarjeta({
  cliente = {}, opciones = [], modo = 'basico', horarios = [], mostrarPrecio = true, mostrarDolares = false, nota,
}) {
  const det = modo === 'detallado';
  const esRev = cliente.tipo === 'revendedor';
  const lista = opciones.slice(0, MAX_OPCIONES);
  const pasajeros = (Array.isArray(cliente.pasajeros) ? cliente.pasajeros : []).map(txt).filter(Boolean);
  const verDolares = mostrarDolares && !esRev && mostrarPrecio;

  const precioDe = (resultado) => {
    if (esRev) return resultado.precioFinal ?? null;
    const usd = esNum(cliente.usd) ? Number(cliente.usd) : null;
    const tasa = esNum(cliente.tasa) ? Number(cliente.tasa) : null;
    return usd === null || tasa === null ? null : usd * tasa;
  };

  const tarjetas = lista.map((it, i) => {
    const faltantes = [];
    const pasos = pasosDe(it, modo, faltantes);
    const precio = precioDe(it.resultado);
    if (mostrarPrecio && precio === null) faltantes.push('precio');
    return {
      id: it.opcion.id, orden: i + 1, pasos, faltantes,
      precio, precioTxt: precio === null ? '' : reales(precio),
      usdTxt: verDolares && esNum(cliente.usd) ? `≈ ${dolares(Number(cliente.usd))}` : '',
      llegadaAbs: llegadaFinalAbs(it.opcion), llegadaTxt: llegadaFinalTxt(it.opcion),
      conBusFinal: pasos.some((p) => p.clave === 'bus2'),
      etiquetaManual: txt(it.opcion.tarjeta_ajustes?.etiqueta),
      escalas: esNum(it.opcion.escalas) ? Number(it.opcion.escalas) : null,
      tag: null, mejor: false,
    };
  });

  // Etiquetas: la de menor precio final = "Más económica" (borde naranja);
  // la que llega antes = "La más rápida"; una sola = "Trayecto completo".
  if (tarjetas.length === 1) tarjetas[0].tag = { texto: 'Trayecto completo', clase: 'blue' };
  else if (tarjetas.length > 1) {
    let barata = -1;
    tarjetas.forEach((t, i) => { if (t.precio !== null && (barata === -1 || t.precio < tarjetas[barata].precio)) barata = i; });
    let rapida = -1;
    tarjetas.forEach((t, i) => { if (t.llegadaAbs !== null && (rapida === -1 || t.llegadaAbs < tarjetas[rapida].llegadaAbs)) rapida = i; });
    // La más cómoda = la de menos escalas, solo si tiene estrictamente menos que la más económica
    // (si la más económica ya es la de menos escalas, no hay nada que ofrecer como "más cómoda").
    let comoda = -1;
    tarjetas.forEach((t, i) => {
      if (t.escalas === null) return;
      if (comoda === -1 || t.escalas < tarjetas[comoda].escalas
        || (t.escalas === tarjetas[comoda].escalas && t.llegadaAbs !== null && (tarjetas[comoda].llegadaAbs === null || t.llegadaAbs < tarjetas[comoda].llegadaAbs))) comoda = i;
    });
    if (comoda === barata || (barata >= 0 && tarjetas[barata].escalas !== null && tarjetas[comoda]?.escalas >= tarjetas[barata].escalas)) comoda = -1;
    if (barata >= 0) { tarjetas[barata].tag = { texto: 'Más económica', clase: 'orange' }; tarjetas[barata].mejor = true; }
    if (comoda >= 0) tarjetas[comoda].tag = { texto: comoda === rapida ? 'Más cómoda y rápida' : 'Más cómoda', clase: 'blue' };
    if (rapida >= 0 && rapida !== barata && rapida !== comoda) tarjetas[rapida].tag = { texto: 'La más rápida', clase: 'blue' };
  }
  tarjetas.forEach((t) => {
    if (t.etiquetaManual) t.tag = { texto: t.etiquetaManual, clase: t.mejor ? 'orange' : 'blue' };
    const sinBusInicial = !t.pasos.some((p) => p.clave === 'bus1');
    t.titulo = sinBusInicial ? (t.conBusFinal ? 'Avión + ómnibus' : 'Avión') : (t.conBusFinal ? 'Ómnibus + avión + ómnibus' : 'Ómnibus + avión');
  });

  const destino = ciudadCorta(lista.map((x) => x.opcion.ciudad).find((c) => txt(c)) || '') || 'tu destino';
  const primerDia = lista.map((x) => x.resultado.bus?.salidaIso || x.opcion.dia_vuelo).filter(Boolean).sort()[0];

  const chips = [];
  if (pasajeros.length) chips.push(`${pasajeros.length} ${pasajeros.length === 1 ? 'pasajero' : 'pasajeros'}`);
  if (primerDia) chips.push(`Desde el ${fechaDiaMes(primerDia)}`);

  const notaFinal = [txt(nota)];
  if (verDolares && cliente.tasa) notaFinal.push(`El equivalente en dólares está calculado a R$ ${fmtBRL(Number(cliente.tasa), 2)} por dólar.`);

  const faltantes = [];
  if (det && !pasajeros.length) faltantes.push('nombre del pasajero');
  return {
    modo, destino, subtitulo: 'Así queda tu viaje', chips,
    pasajero: det ? pasajeros.join(', ') : '',
    mostrarPrecio, totalEtiqueta: pasajeros.length > 1 ? `Total ${pasajeros.length} pasajeros` : 'Total',
    opciones: tarjetas,
    nota: notaFinal.filter(Boolean).join(' '),
    faltantes: [...new Set([...faltantes, ...tarjetas.flatMap((t) => t.faltantes)])],
  };
}

// Marca/desmarca una opción respetando el máximo de 3.
export function alternarOpcion(ids, id) {
  if (ids.includes(id)) return ids.filter((x) => x !== id);
  return ids.length >= MAX_OPCIONES ? ids : [...ids, id];
}

// Texto plano de toda la tarjeta (para revisar qué sale sin dibujarla).
export function textoTarjeta(m) {
  return [
    m.destino, m.chips.join(' '), m.pasajero,
    ...m.opciones.flatMap((o) => [o.tag?.texto, ...o.pasos.map((p) => p.texto), o.llegadaTxt, m.mostrarPrecio ? o.precioTxt : '', o.usdTxt]),
    m.nota,
  ].filter(Boolean).join('\n');
}

// ── HTML (plantilla aprobada) ────────────────────────────────────────
const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// El CSS de la plantilla, con todos los selectores bajo `.tc` para que no
// toque el resto de la app. `body` pasa a ser `.tc`.
const REGLAS = [
  ['', '--blue:#064985;--blue2:#0A63B3;--orange:#F46D0D;--ink:#0B2545;--mute:#5A6B82;width:1080px;font-family:\'Poppins\',sans-serif;background:#EEF3F9;color:var(--ink);text-align:left;line-height:normal'],
  ['*', 'box-sizing:border-box;margin:0;padding:0'],
  ['.wrap', 'padding:0 0 40px'],
  ['.top', 'background:#fff;padding:36px 0 26px;text-align:center'],
  ['.top img', 'width:470px'],
  ['.band', 'background:linear-gradient(135deg,var(--blue),var(--blue2));border-top:8px solid var(--orange);color:#fff;padding:44px 64px 76px;text-align:center'],
  ['.band h1', 'font-size:54px;line-height:1.15;font-weight:700'],
  ['.band h1 em', 'font-style:normal;color:#FFB27A'],
  ['.band .sub', 'margin-top:14px;font-size:26px;color:#D3E3F5'],
  ['.chips', 'margin-top:26px;display:flex;gap:14px;justify-content:center;flex-wrap:wrap'],
  ['.chip', 'background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.3);border-radius:40px;padding:8px 24px;font-size:22px;font-weight:500'],
  ['.cards', 'padding:0 44px;margin-top:-40px;position:relative;z-index:2'],
  ['.card', 'background:#fff;border-radius:28px;box-shadow:0 10px 30px rgba(11,37,69,.13);margin-bottom:30px;overflow:hidden'],
  ['.card.best', 'border:3px solid var(--orange)'],
  ['.card-top', 'display:flex;justify-content:space-between;align-items:flex-start;padding:30px 36px 8px'],
  ['.opt', 'font-size:21px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:#6B7C93'],
  ['.title', 'font-size:34px;font-weight:700;margin-top:4px;line-height:1.2;color:var(--blue)'],
  ['.tag', 'font-size:20px;font-weight:600;padding:8px 20px;border-radius:30px;white-space:nowrap;margin-top:4px'],
  ['.tag.orange', 'background:#FDE6D3;color:#C4500A'],
  ['.tag.blue', 'background:#DCE9F7;color:var(--blue)'],
  ['.tag.solid', 'background:var(--blue);color:#fff'],
  ['.steps', 'padding:14px 36px 8px'],
  ['.step', 'display:flex;gap:20px;padding:10px 0'],
  ['.dotcol', 'width:22px;display:flex;flex-direction:column;align-items:center'],
  ['.dot', 'width:16px;height:16px;border-radius:50%;margin-top:10px;flex-shrink:0;background:var(--blue)'],
  ['.dot.fly', 'background:var(--orange)'],
  ['.dot.wait', 'background:#fff;border:3px solid #8B9BB0'],
  ['.line', 'width:3px;background:#D5DEEA;flex:1;margin-top:4px'],
  ['.step:last-child .line', 'display:none'],
  ['.stxt', 'font-size:24px;line-height:1.35;padding-bottom:6px'],
  ['.stxt b', 'font-weight:600'],
  ['.stxt span', 'color:var(--mute);font-size:21px;display:block'],
  ['.stxt.wait b', 'color:#4A5B73;font-weight:500;font-style:italic'],
  ['.bottom', 'display:flex;justify-content:space-between;align-items:center;background:#F4F8FD;border-top:1px solid #E1E9F3;padding:22px 36px;margin-top:10px'],
  ['.lbl', 'font-size:19px;color:#6B7C93;text-transform:uppercase;letter-spacing:1.5px;font-weight:500'],
  ['.arrive .val', 'font-size:27px;font-weight:700;margin-top:2px'],
  ['.price', 'text-align:right'],
  ['.price .val', 'font-size:46px;font-weight:700;color:var(--blue);line-height:1.1;margin-top:2px'],
  ['.price .pp', 'font-size:21px;color:var(--mute);margin-top:6px'],
  ['.note', 'margin:6px 44px 0;padding:26px 34px;background:#fff;border-radius:22px;border-left:8px solid var(--orange);font-size:21px;line-height:1.55;color:#46586F'],
  ['.note b', 'color:var(--ink);font-weight:600'],
  ['.footer', 'text-align:center;margin-top:30px;font-size:21px;color:#6B7C93;line-height:1.6'],
  ['.footer .name', 'font-weight:700;color:var(--blue);font-size:24px'],
];
export const TARJETA_CSS = REGLAS.map(([sel, cuerpo]) => `.tc${sel ? ` ${sel}` : ''}{${cuerpo}}`).join('');

const clasePunto = { bus: '', espera: ' wait', vuelo: ' fly', traslado: '' };

function htmlPaso(p) {
  const espera = p.tipo === 'espera';
  const spans = p.lineas.map((l) => `<span>${esc(l)}</span>`).join('');
  return `<div class="step"><div class="dotcol"><div class="dot${clasePunto[p.tipo] || ''}"></div><div class="line"></div></div>`
    + `<div class="stxt${espera ? ' wait' : ''}"><b>${esc(p.titulo)}</b>${spans}</div></div>`;
}

export function tarjetaHtml(m, { logoUrl = 'logo-tarjeta.png' } = {}) {
  const unica = m.opciones.length === 1;
  const cards = m.opciones.map((o) => {
    const precio = m.mostrarPrecio && o.precioTxt
      ? `<div class="price"><div class="lbl">${esc(m.totalEtiqueta)}</div><div class="val">${esc(o.precioTxt)}</div>${o.usdTxt ? `<div class="pp">${esc(o.usdTxt)}</div>` : ''}</div>`
      : '';
    const llegada = o.llegadaTxt ? `<div class="arrive"><div class="lbl">Llegada a ${esc(m.destino)}</div><div class="val">${esc(o.llegadaTxt)}</div></div>` : '<div></div>';
    return `<div class="card${o.mejor ? ' best' : ''}"><div class="card-top"><div><div class="opt">${unica ? 'Opción única' : `Opción ${o.orden}`}</div>`
      + `<div class="title">${esc(o.titulo)}</div></div>${o.tag ? `<div class="tag ${o.tag.clase}">${esc(o.tag.texto)}</div>` : ''}</div>`
      + `<div class="steps">${o.pasos.map(htmlPaso).join('')}</div><div class="bottom">${llegada}${precio}</div></div>`;
  }).join('');
  const chips = [...m.chips, m.pasajero ? `Pasajero: ${m.pasajero}` : ''].filter(Boolean).map((c) => `<div class="chip">${esc(c)}</div>`).join('');
  return `<div class="tc"><div class="wrap"><div class="top"><img src="${esc(logoUrl)}" alt="Click Travel Agency"></div>`
    + `<div class="band"><h1>Opción de viaje<br>Boa Vista → <em>${esc(m.destino)}</em></h1><div class="sub">${esc(m.subtitulo)}</div>`
    + `${chips ? `<div class="chips">${chips}</div>` : ''}</div><div class="cards">${cards}</div>`
    + `${m.nota ? `<div class="note">${esc(m.nota)}</div>` : ''}`
    + '<div class="footer"><div class="name">Click Travel Agency</div>clicktravelagency.com.br · reservas@clicktravelagency.com.br</div></div></div>';
}

export const nombreArchivo = (cliente, modo) => {
  const limpio = txt(cliente).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'cliente';
  return `orcamento-click-travel-${limpio}-${modo === 'detallado' ? 'detallado' : 'basico'}.png`;
};
